
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';
import { formatCurrency as formatCurrencyUtil } from '@/utils/currency';

// Create a singleton instance to avoid multiple client creations
const SUPABASE_URL = "https://kytuiixerbnmpvcqpmcj.supabase.co";
const SUPABASE_PUBLISHABLE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imt5dHVpaXhlcmJubXB2Y3FwbWNqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDE3Njk1MzcsImV4cCI6MjA1NzM0NTUzN30.ht4WVioysuwPVagjWKiaHjMgxzJMvD1O1NRz3sUcf5g";

// Performance optimization: Add request timeout and retry logic
export const supabase = createClient<Database>(
  SUPABASE_URL, 
  SUPABASE_PUBLISHABLE_KEY,
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
    },
    global: {
      // Add timeout to prevent hanging requests
      fetch: (...args) => {
        const startTime = performance.now();
        const controller = new AbortController();
        const { signal } = controller;
        
        // Set a timeout for requests (10 seconds)
        const timeoutId = setTimeout(() => {
          controller.abort();
          console.warn('Supabase request timeout - aborted after 10s');
        }, 10000);
        
        const fetchOptions = args[1] || {};
        const enhancedOptions = {
          ...fetchOptions,
          signal
        };
        
        return fetch(args[0], enhancedOptions)
          .then(response => {
            const endTime = performance.now();
            const url = args[0] as string;
            const endpoint = url.split('/').pop() || url;
            
            clearTimeout(timeoutId);
            
            if (endTime - startTime > 500) {
              console.info(`Slow Supabase query (${(endTime - startTime).toFixed(2)}ms): ${endpoint}`);
            }
            
            return response;
          })
          .catch(error => {
            clearTimeout(timeoutId);
            console.error('Supabase fetch error:', error);
            throw error;
          });
      }
    }
  }
);

// Create or update profile for current user
export const ensureUserProfile = async () => {
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  
  if (userError || !user) {
    console.error('Error checking user:', userError);
    return null;
  }
  
  // Check if profile exists
  const { data: existingProfile, error: profileError } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', user.id)
    .maybeSingle();
    
  if (profileError && profileError.code !== 'PGRST116') {
    console.error('Error checking profile:', profileError);
    return null;
  }
  
  // If profile doesn't exist, create one
  if (!existingProfile) {
    const { data: newProfile, error: insertError } = await supabase
      .from('profiles')
      .insert([{ id: user.id }])
      .select()
      .single();
      
    if (insertError) {
      console.error('Error creating profile:', insertError);
      return null;
    }
    
    return newProfile;
  }
  
  return existingProfile;
};

// Utility functions for formatting
export const formatCurrency = (amount: number): string => {
  try {
    return formatCurrencyUtil(amount);
  } catch (error) {
    // Fallback in case of error
    return `$${amount.toFixed(2)}`;
  }
};

export const formatDate = (dateString: string): string => {
  const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };
  return new Date(dateString).toLocaleDateString('en-US', options);
};

export const getDueStatus = (dueDate: string): { text: string; status: 'normal' | 'warning' | 'urgent' | 'overdue' } => {
  const today = new Date();
  const due = new Date(dueDate);
  const diffTime = due.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays < 0) {
    return { text: `Overdue by ${Math.abs(diffDays)} days`, status: 'overdue' };
  } else if (diffDays === 0) {
    return { text: 'Due today', status: 'urgent' };
  } else if (diffDays <= 3) {
    return { text: `Due in ${diffDays} days`, status: 'warning' };
  } else {
    return { text: `Due in ${diffDays} days`, status: 'normal' };
  }
};
